import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import {
  classifyManualTurnFailure,
  useSendManualTurnMutation,
  type ManualTurnFailure,
  type PreviewManualSessionState,
} from "@/api/previewManual";
import type { LabPendingLine } from "@/lib/labModel";
import { recallRun } from "@/lib/labRunMemory";
import {
  MANUAL_STATUS_LABELS,
  manualBlocker,
  manualRequestBody,
  nextAttempt,
  readManualText,
  type ManualAttempt,
} from "@/lib/previewManual";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Textarea } from "@/ui/Input";

import { MicButton, useDictation } from "./LabVoiceMic";

const TURN_FAILURE_MESSAGES = {
  busy: "Liv is still replying, or a replay is running. The state was re-read: send again when it is ready.",
  unanswered:
    "Liv's reply to your last line failed, so this conversation cannot take another line.",
  stale:
    "The conversation moved on since you opened it. The state was re-read: check the latest turn, then send again.",
  ended: "This conversation has ended.",
  not_found: "This session is no longer available.",
  denied: "Your account cannot send turns in this project.",
  unknown:
    "The service did not confirm whether your line was delivered. Retry: the same line cannot be delivered twice.",
  error: "The line could not be sent.",
} as const satisfies Record<ManualTurnFailure["kind"], string>;

/**
 * The Lab's composer for a manual session: the clinician plays the patient.
 *
 * Enter sends, Shift+Enter starts a new line. One turn is in flight at a
 * time: the control waits for Liv's reply, and the service answers a second
 * submit 409.
 *
 * The draft lives only in this component's state — never in storage, the
 * address, a query key or a log line. A send is an ATTEMPT (idempotency key +
 * expected turn): an unchanged draft after an inconclusive answer re-presents
 * the same attempt, so a turn that did land is reported as replayed instead of
 * being submitted twice.
 */
export function LabComposer({
  projectId,
  previewRunRef,
  manual,
  followLive,
  onFollowLiveChange,
  language = "he",
  onPendingLine,
  actions,
  continueFrom,
}: {
  projectId: string;
  previewRunRef: string;
  manual: PreviewManualSessionState;
  followLive: boolean;
  onFollowLiveChange: (follow: boolean) => void;
  /** The session's language (`he` or `en`); picks the dictation language. */
  language?: string;
  /** Told about the line being sent, so the conversation can show it at once. */
  onPendingLine?: (line: LabPendingLine | null) => void;
  /** Shown beside Send. */
  actions?: ReactNode;
  /** The control that continues a new session from the last answered turn, given that turn's number. */
  continueFrom?: (turnNumber: number) => ReactNode;
}) {
  const ids = useId();
  const send = useSendManualTurnMutation(projectId, previewRunRef);
  const [text, setText] = useState("");
  const attempt = useRef<ManualAttempt | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const [autoSend, setAutoSend] = useState(false);
  const dictation = useDictation({
    projectId,
    previewRunRef,
    language,
    getText: () => text,
    onText: setText,
    onFinished: (heard) => {
      // The events that filled the draft were flushed before the engine ended.
      if (heard && autoSend) {
        submit();
      }
    },
  });

  const restoreThrough = recallRun(previewRunRef).restoreThrough ?? null;
  const blocker = manualBlocker(manual, text, restoreThrough);
  const line = readManualText(text);
  const sending = send.isPending;
  // Closed until Liv has answered: a line typed earlier would answer the next question.
  const waiting = sending || manualBlocker(manual, "", restoreThrough) !== null;
  const turnFailure = send.isError
    ? classifyManualTurnFailure(send.error)
    : null;
  // Sending again after "unanswered" is refused for good; typing stays possible.
  const canSend =
    blocker === null &&
    line !== null &&
    !sending &&
    turnFailure?.kind !== "unanswered";
  const lastAnswered =
    turnFailure?.kind === "unanswered" ? (turnFailure.currentTurn ?? 0) : 0;

  // After a turn that landed, the next line starts from the textarea.
  const wasWaiting = useRef(false);
  useEffect(() => {
    if (wasWaiting.current && !waiting && !send.isError) {
      input.current?.focus();
    }
    wasWaiting.current = waiting;
  }, [waiting, send.isError]);

  function submit() {
    if (!canSend || line === null) {
      return;
    }
    attempt.current = nextAttempt(attempt.current, line, manual);
    const current = attempt.current;
    const pendingLine = (status: LabPendingLine["status"]): LabPendingLine => ({
      previewRunRef,
      text: current.text,
      expectedTurn: current.expectedTurn,
      status,
    });
    setText("");
    onPendingLine?.(pendingLine("sending"));
    send.mutate(manualRequestBody(current), {
      onSuccess() {
        attempt.current = null;
        onPendingLine?.(pendingLine("sent"));
      },
      onError(error) {
        setText(current.text);
        const kind = classifyManualTurnFailure(error).kind;
        onPendingLine?.(
          pendingLine(kind === "unknown" ? "unconfirmed" : "failed"),
        );
        // Only an inconclusive answer keeps the attempt for a same-key retry.
        if (kind !== "unknown" && kind !== "busy") {
          attempt.current = null;
        }
      },
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      submit();
    }
  }


  const textId = `${ids}-text`;
  const reasonId = `${ids}-reason`;
  const formId = `${ids}-form`;

  return (
    <div className="sticky -bottom-3 -mx-3 space-y-2 border-t border-border bg-background px-3 pb-3 pt-3">
      <form
        id={formId}
        aria-label="Compose the patient's next line"
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-wrap items-center justify-end gap-3 text-sm">
          <Badge tone={manual.status === "ready" ? "outline" : "warning"}>
            {MANUAL_STATUS_LABELS[manual.status]}
          </Badge>
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={followLive}
              onChange={(event) => onFollowLiveChange(event.target.checked)}
            />
            Follow live
          </label>
        </div>
        <label htmlFor={textId} className="sr-only">
          Patient line
        </label>
        <Textarea
          id={textId}
          ref={input}
          dir="auto"
          rows={3}
          value={text}
          readOnly={waiting}
          placeholder="Type what the patient says. Enter sends, Shift+Enter adds a line."
          aria-describedby={blocker === null ? undefined : reasonId}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
        />
      </form>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="submit"
          form={formId}
          disabled={!canSend}
          title={blocker ?? undefined}
        >
          {sending
            ? "Sending…"
            : turnFailure?.kind === "unknown"
              ? "Retry send"
              : "Send"}
        </Button>
        <MicButton
          supported={dictation.supported}
          listening={dictation.listening}
          disabled={
            waiting || dictation.transcribing
          }
          onClick={dictation.toggle}
        />
        {actions}
        {dictation.supported ? (
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={autoSend}
              onChange={(event) => setAutoSend(event.target.checked)}
            />
            Send automatically when I stop talking
          </label>
        ) : null}
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-muted-foreground"
        >
          {dictation.listening
            ? "Listening…"
            : dictation.transcribing
              ? "Transcribing…"
              : ""}
        </p>
      </div>
      {dictation.error !== null ? (
        <p role="alert" className="text-sm text-destructive">
          {dictation.error}
        </p>
      ) : null}
      {blocker !== null ? (
        <p id={reasonId} className="text-sm text-muted-foreground">
          {blocker}
        </p>
      ) : null}
      {turnFailure !== null && !(turnFailure.kind === "busy" && blocker !== null) ? (
        <p role="alert" className="text-sm text-destructive">
          {TURN_FAILURE_MESSAGES[turnFailure.kind]}
          {turnFailure.kind === "unanswered"
            ? lastAnswered >= 1
              ? " Continue from the last answered turn to keep going."
              : " Start a new conversation to keep going."
            : null}
        </p>
      ) : null}
      {turnFailure?.kind === "unanswered" && lastAnswered >= 1
        ? continueFrom?.(lastAnswered)
        : null}
    </div>
  );
}

/** The composer's place while no conversation is active: the same controls, all disabled. */
export function LabComposerIdle() {
  const id = useId();
  return (
    <div className="sticky -bottom-3 -mx-3 space-y-2 border-t border-border bg-background px-3 pb-3 pt-3">
      <label htmlFor={id} className="sr-only">
        Patient line
      </label>
      <Textarea
        id={id}
        dir="auto"
        rows={3}
        disabled
        placeholder="Start a new conversation to type"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" disabled>
          Send
        </Button>
        <MicButton
          supported
          listening={false}
          disabled
          onClick={() => undefined}
        />
        <Button type="button" variant="outline" size="sm" disabled>
          Undo
        </Button>
      </div>
    </div>
  );
}
